import * as signalR from '@aspnet/signalr';
import store from '../store';

let signalRConnection: signalR.HubConnection;
let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
let hasConnectedOnce = false;
const MAX_RECONNECT_DELAY_MS = 60000;
const BASE_RECONNECT_DELAY_MS = 5000;
const MAX_RECONNECT_ATTEMPTS = 5;

function reconnect(conn: signalR.HubConnection) {
  // Cancel any pending retry so we don't stack timeouts.
  if (reconnectTimeoutId !== null) {
    clearTimeout(reconnectTimeoutId);
    reconnectTimeoutId = null;
  }

  // Only retry reconnects if we've successfully connected before,
  // and cap attempts to avoid hammering a down endpoint.
  if (!hasConnectedOnce || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.warn(
      '[MetricsHub] Giving up reconnect after',
      reconnectAttempts,
      'attempts'
    );
    return;
  }

  const delay = Math.min(
    BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts),
    MAX_RECONNECT_DELAY_MS
  );
  reconnectAttempts++;
  reconnectTimeoutId = setTimeout(() => {
    reconnectTimeoutId = null;
    conn
      .start()
      .then(() => {
        reconnectAttempts = 0;
      })
      .catch((e: any) => {
        console.error('[MetricsHub] Reconnect failed:', e);
        reconnect(conn);
      });
  }, delay);
}

let initialized = false;

export default {
  init() {
    // Prevent duplicate init — avoids stacking onclose handlers.
    if (initialized && signalRConnection) {
      return;
    }

    const token: string = store.getters.token;
    signalRConnection = new signalR.HubConnectionBuilder()
      .withUrl(process.env.VUE_APP_HUB_ROOT + '/metricshub', {
        accessTokenFactory: () => token,
      })
      .build();
    signalRConnection.onclose(() => {
      reconnect(signalRConnection);
    });
    initialized = true;

    // Initial connect — only enable reconnect loop if this succeeds.
    signalRConnection
      .start()
      .then(() => {
        hasConnectedOnce = true;
        reconnectAttempts = 0;
      })
      .catch((e: any) => {
        console.error('[MetricsHub] Initial connection failed:', e);
        // Don't retry — endpoint may not exist yet.
      });
  },
  getConnection() {
    return signalRConnection;
  },
  disconnect() {
    if (reconnectTimeoutId !== null) {
      clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
    reconnectAttempts = 0;
    hasConnectedOnce = false;
    initialized = false;
    if (signalRConnection) {
      signalRConnection.stop();
    }
  },
};
